import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { ClipboardText, SignIn, SignOut, FilmSlate, Television, Broadcast, Palette, UserGear, Gear } from "@phosphor-icons/react";

const ACTION_META = {
  login: { label: "Signed in", icon: SignIn, cls: "text-emerald-500" },
  logout: { label: "Signed out", icon: SignOut, cls: "text-neutral-400" },
  setup_completed: { label: "Completed setup", icon: Gear, cls: "text-[#3A86FF]" },
  asset_created: { label: "Created asset", icon: FilmSlate, cls: "text-emerald-500" },
  asset_deleted: { label: "Deleted asset", icon: FilmSlate, cls: "text-red-500" },
  channel_created: { label: "Created channel", icon: Television, cls: "text-emerald-500" },
  channel_deleted: { label: "Deleted channel", icon: Television, cls: "text-red-500" },
  channel_status_changed: { label: "Changed channel status", icon: Television, cls: "text-amber-500" },
  channel_published: { label: "Published channel", icon: Broadcast, cls: "text-[#3A86FF]" },
  brand_updated: { label: "Updated branding", icon: Palette, cls: "text-amber-500" },
  organization_updated: { label: "Updated organization", icon: Gear, cls: "text-amber-500" },
  user_created: { label: "Created user", icon: UserGear, cls: "text-emerald-500" },
  user_updated: { label: "Updated user", icon: UserGear, cls: "text-amber-500" },
  user_disabled: { label: "Disabled user", icon: UserGear, cls: "text-red-500" },
  user_enabled: { label: "Enabled user", icon: UserGear, cls: "text-emerald-500" },
  user_deleted: { label: "Deleted user", icon: UserGear, cls: "text-red-500" },
  password_reset: { label: "Reset password", icon: UserGear, cls: "text-amber-500" },
  ownership_transferred: { label: "Transferred ownership", icon: UserGear, cls: "text-[#3A86FF]" },
  config_exported: { label: "Exported configuration", icon: Gear, cls: "text-[#3A86FF]" },
  config_imported: { label: "Imported configuration", icon: Gear, cls: "text-[#3A86FF]" },
};

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AuditLog() {
  const [logs, setLogs] = useState(null);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = (a) => api.get("/audit", { params: a && a !== "all" ? { action: a } : {} }).then((r) => setLogs(r.data));
  useEffect(() => { load("all"); api.get("/audit/actions").then((r) => setActions(r.data)); }, []);

  const onFilter = (e) => { setFilter(e.target.value); setLogs(null); load(e.target.value); };

  return (
    <>
      <PageHeader title="Audit Log" subtitle="System activity history" actions={
        <select data-testid="audit-filter" value={filter} onChange={onFilter}
          className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-1.5 text-xs outline-none">
          <option value="all">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>)}
        </select>
      } />
      <div className="p-8 max-w-4xl ccs-enter" data-testid="audit-log-page">
        <div className="bg-[#141414] border border-[#262626] rounded-sm">
          {!logs ? <Loading /> : logs.length === 0 ? (
            <div className="text-center text-[#737373] text-sm py-16" data-testid="audit-empty">No activity recorded yet.</div>
          ) : (
            logs.map((l) => {
              const meta = ACTION_META[l.action] || { label: l.action, icon: ClipboardText, cls: "text-neutral-400" };
              const Icon = meta.icon;
              return (
                <div key={l.id} data-testid={`audit-row-${l.id}`} className="flex items-start gap-3 px-5 py-3 border-b border-[#1c1c1c] last:border-0 hover:bg-[#1F1F1F] transition-colors duration-150">
                  <div className={`h-8 w-8 rounded-sm bg-[#0A0A0A] border border-[#262626] flex items-center justify-center ${meta.cls}`}><Icon size={16} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className={meta.cls}>{meta.label}</span>
                      {l.detail && Object.keys(l.detail).length > 0 && (
                        <div className="text-[#737373] font-mono text-xs mt-0.5 break-all">
                          {Object.entries(l.detail).map(([k, v]) => `${k}=${v}`).join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-[#525252]">{l.actor_name || l.actor_email || "system"}</div>
                  </div>
                  <div className="font-mono text-[10px] text-[#525252] whitespace-nowrap">{fmtTime(l.created_at)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
